import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Auth } from "aws-amplify";
import { getPageContent } from "../../utils";
import { useAuth } from "../../hooks/useAuth";
import {
    selectApplicantDetailsData,
    selectApplicantDetailsStatus,
    selectApplicantDetailsError,
    initializeAsync,
} from "./applicantDetailsSlice";

import { get, post } from "../../api";
import { describe } from "../../services/salesforce";

// Add custom imports here

import {
    Button,
    CssBaseline,
    Box,
    Paper,
    Menu,
    MenuItem,
    Container,
    Typography,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Backdrop,
    CircularProgress,
    Alert,
    AlertTitle,
    Snackbar,
} from "@mui/material";

import { Input } from "../../components/Input";
import LoadingButton from "@mui/lab/LoadingButton";
import { updateApplicant } from "../../utils";

import { init, FullStory } from "@fullstory/browser";
init({ orgId: process.env.REACT_APP_FULL_STORY_ORG_ID });

const ApplicantDetails = () => {
    const data = useSelector(selectApplicantDetailsData);
    const status = useSelector(selectApplicantDetailsStatus);
    const error = useSelector(selectApplicantDetailsError);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { passwordLessLogin, login, isAuthenticated } = useAuth();
    const [openToster, setOpenToster] = useState(false);
    const [tosterVariant, setTosterVariant] = useState("error");
    const [tosterMessage, setTosterMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState({
        firstName: "",
        lastName: "",
        email: location?.state?.searchParams?.email?.trim(),
    });
    const [marketList, setMarketList] = useState([]);
    const [moveInList, setMoveInList] = useState([]);
    const [aboutPathway, setAboutPathway] = useState([]);

    const firstNameRef = useRef();
    const lastNameRef = useRef();
    const phoneRef = useRef();
    const marketRef = useRef();
    const moveInDateRef = useRef();
    const howDidYouHearRef = useRef();

    const refs = [firstNameRef, lastNameRef, phoneRef, marketRef, moveInDateRef, howDidYouHearRef];

    // Add Variables
    // App deployment

    useEffect(() => {
        if (status === "idle") {
            dispatch(initializeAsync());
            getLeadDescribe();
        }
    }, [status, dispatch]);

    if (!location?.state?.searchParams?.email) {
        window.location.href = "/login";
        return;
    }

    const getLeadDescribe = async () => {
        const result = await describe("lead", { cacheable: true });
        if (result.isSuccess) {
            for (let field of result?.data?.fields) {
                if (field.name === "Target_Market__c") {
                    setMarketList(field.picklistValues);
                } else if (field.name === "What_is_your_timeframe_to_move_in__c") {
                    setMoveInList(field.picklistValues);
                } else if (field.name === "How_did_you_hear_about_Pathway__c") {
                    setAboutPathway(field.picklistValues);
                }
            }
        }
    };

    const handleChange = (key, value) => {
        let data = { ...details };
        data[key] = value;

        setDetails(data);
    };

    const handleTosterClose = () => {
        setOpenToster(false);
    };

    const getCookie = (cookieName) => {
        // Split cookies string into individual cookies
        var cookies = document.cookie.split(";");

        // Loop through each cookie
        for (var i = 0; i < cookies.length; i++) {
            var cookie = cookies[i].trim();

            // Check if this cookie is the one we're looking for
            if (cookie.indexOf(cookieName + "=") === 0) {
                // Extract and return the cookie value
                return cookie.substring(cookieName.length + 1);
            }
        }

        // If the cookie is not found, return null
        return null;
    };

    const handleClick = async () => {
        let authenticated = await isAuthenticated();
        if (authenticated) {
            window.location.href = "/";
            return;
        }
        if (validate()) {
            setLoading(true);
            let record = { ...details };

            var gclid = getCookie("gclid");
            var fbclid = getCookie("fbclid");

            if (gclid) {
                record.gclid = gclid;
            }

            if (fbclid) {
                record.fbclid = fbclid;
            }

            let adParams = getCookie("adParams");
            if (adParams) {
                let paramValues = new URLSearchParams(adParams);
                // if (paramValues.get("gclid")) {
                //     record.gclid = paramValues.get("gclid");
                // }
                // if (paramValues.get("fbclid")) {
                //     record.fbclid = paramValues.get("fbclid");
                // }

                let utmParameters = {};
                if (paramValues.get("utm_source")) {
                    utmParameters["utm_source"] = paramValues.get("utm_source");
                }
                if (paramValues.get("utm_medium")) {
                    utmParameters["utm_medium"] = paramValues.get("utm_medium");
                }
                if (paramValues.get("utm_campaign")) {
                    utmParameters["utm_campaign"] = paramValues.get("utm_campaign");
                }
                if (paramValues.get("utm_content")) {
                    utmParameters["utm_content"] = paramValues.get("utm_content");
                }
                if (paramValues.get("utm_term")) {
                    utmParameters["utm_term"] = paramValues.get("utm_term");
                }
                if (Object.keys(utmParameters).length > 0) {
                    record.utmParameters = JSON.stringify(utmParameters);
                }
            }

            post(`lead/createLead`, record).then(async (result) => {
                if (result.isSuccess && result.data) {
                    passwordLessLogin({
                        email: record.email,
                        contactData: {
                            email: record.email,
                            phone: record.phone,
                            id: result.data.Id,
                            objectType: "Lead",
                        },
                        callback: (failed) => {
                            if (failed) {
                                setTosterVariant("error");
                                setTosterMessage(`Sorry, we don't recognize this email address`);
                                setOpenToster(true);
                            }
                            setLoading(false);
                        },
                    });
                } else {
                    setTosterVariant("error");
                    setTosterMessage(
                        `There was a problem with starting your application. Please contact support at (877) 958-1888.`
                    );
                    setOpenToster(true);
                    setLoading(false);
                }
            });
        }
    };

    const validate = () => {
        let hasError = false;
        for (let i = 0; i < refs.length; i++) {
            if (!refs[i].current.validate()) {
                hasError = true;
            }
        }
        return !hasError;
    };

    const getMarketOption = () => {
        return marketList;
        // return [
        //     {
        //         value: "Atlanta",
        //         label: "Atlanta",
        //     },
        //     {
        //         value: "Carolinas",
        //         label: "Carolinas",
        //     },
        //     {
        //         value: "Dallas",
        //         label: "Dallas",
        //     },
        //     {
        //         value: "Orlando",
        //         label: "Orlando",
        //     },
        //     {
        //         value: "Phoenix",
        //         label: "Phoenix",
        //     },
        //     {
        //         value: "Tampa",
        //         label: "Tampa",
        //     },
        //     {
        //         value: "Other",
        //         label: "Other",
        //     },
        // ];
    };

    const getMoveInTimeOption = () => {
        return moveInList;
        // return [
        //     {
        //         value: "ASAP",
        //         label: "ASAP",
        //     },
        //     {
        //         value: "1 month",
        //         label: "1 month",
        //     },
        //     {
        //         value: "2-3 Months",
        //         label: "2-3 Months",
        //     },
        //     {
        //         value: "4+ Months",
        //         label: "4+ Months",
        //     },
        //     {
        //         value: "Not sure",
        //         label: "Not sure",
        //     },
        // ];
    };

    const getHowDidYouHearOption = () => {
        return aboutPathway;
        // return [
        //     {
        //         value: "Zillow / Available Rental",
        //         label: "Zillow / Available Rental",
        //     },
        //     {
        //         value: "Social Media",
        //         label: "Social Media",
        //     },
        //     {
        //         value: "Event",
        //         label: "Event",
        //     },
        //     {
        //         value: "News Article",
        //         label: "News Article",
        //     },
        //     {
        //         value: "Online Ad",
        //         label: "Online Ad",
        //     },
        //     {
        //         value: "Search Engine",
        //         label: "Search Engine",
        //     },
        //     {
        //         value: "Word of Mouth",
        //         label: "Word of Mouth",
        //     },
        //     {
        //         value: "Facebook",
        //         label: "Facebook",
        //     },
        //     {
        //         value: "Instagram",
        //         label: "Instagram",
        //     },
        //     {
        //         value: "Search Agent",
        //         label: "Search Agent",
        //     },
        //     {
        //         value: "Real Estate Agent",
        //         label: "Real Estate Agent",
        //     },
        //     {
        //         value: "Family or Friend",
        //         label: "Family or Friend",
        //     },
        //     {
        //         value: "Print Ad",
        //         label: "Print Ad",
        //     },
        //     {
        //         value: "Radio",
        //         label: "Radio",
        //     },
        //     {
        //         value: "Direct Mail",
        //         label: "Direct Mail",
        //     },
        //     {
        //         value: "Campaign Code",
        //         label: "Campaign Code",
        //     },
        // ];
    };

    let body = (
        <Box
            component="main"
            sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
            }}>
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexGrow: 1,
                    minHeight: "100%",
                }}>
                <Container maxWidth="sm">
                    <CssBaseline />
                    <Paper
                        elevation={16}
                        sx={{
                            backgroundColor: "rgb(255, 255, 255)",
                            color: "rgb(18, 24, 40)",
                            transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                            borderRadius: "8px",
                            boxShadow: "rgb(100 116 139 / 12%) 0px 10px 15px",
                            backgroundImage: "none",
                            overflow: "hidden",
                            padding: "32px",
                            width: "100%",
                        }}>
                        <Box sx={{ textAlign: "center" }}>
                            <Typography component="h1" variant="h4">
                                Sign up!
                            </Typography>
                            <Typography
                                align="center"
                                color="textSecondary"
                                variant="body1"
                                fontSize="0.875rem"
                                marginTop="1rem">
                                Let's create an account to start your application.
                            </Typography>
                        </Box>
                        <Box validate sx={{ mt: 1 }}>
                            <Input
                                id="firstName"
                                label="First name"
                                name="firstName"
                                margin="normal"
                                isRequired={true}
                                value={details.firstName}
                                autoFocus
                                ref={firstNameRef}
                                fullWidth
                                autoComplete="off"
                                onChange={(value) => handleChange("firstName", value)}
                            />

                            <Input
                                id="lastName"
                                label="Last name"
                                name="lastName"
                                margin="normal"
                                ref={lastNameRef}
                                isRequired={true}
                                value={details.lastName}
                                autoFocus
                                fullWidth
                                autoComplete="off"
                                onChange={(value) => handleChange("lastName", value)}
                            />

                            <Input
                                id="phone"
                                type="maskType"
                                margin="normal"
                                ref={phoneRef}
                                label={"Phone Number"}
                                autoComplete="off"
                                onChange={(value) => handleChange("phone", value)}
                                mask={"(999) 999-9999"}
                                isRequired={true}></Input>

                            <Input
                                id="market"
                                type="select"
                                margin="normal"
                                ref={marketRef}
                                label={"Market"}
                                options={getMarketOption()}
                                autoComplete="off"
                                onChange={(value) => handleChange("market", value)}
                                isRequired={true}></Input>

                            <Input
                                id="moveInDate"
                                type="select"
                                margin="normal"
                                ref={moveInDateRef}
                                label={"What is your timeframe to move-in?"}
                                options={getMoveInTimeOption()}
                                autoComplete="off"
                                onChange={(value) => handleChange("moveInDate", value)}
                                isRequired={true}></Input>

                            <Input
                                id="howDidYouHear"
                                type="select"
                                margin="normal"
                                ref={howDidYouHearRef}
                                label={"How did you hear about Pathway Homes?"}
                                options={getHowDidYouHearOption()}
                                autoComplete="off"
                                onChange={(value) => handleChange("howDidYouHear", value)}
                                isRequired={true}></Input>

                            <LoadingButton
                                size="small"
                                type="submit"
                                color="primary"
                                disabled={
                                    details?.firstName &&
                                    details?.lastName &&
                                    details?.phone &&
                                    details?.market &&
                                    details?.moveInDate
                                        ? false
                                        : true
                                }
                                fullWidth
                                loading={loading}
                                loadingPosition="end"
                                variant="contained"
                                onClick={handleClick}
                                sx={{ mt: 3, mb: 2, height: "45px" }}>
                                Submit
                            </LoadingButton>
                        </Box>
                        <Typography
                            align="center"
                            color="textSecondary"
                            variant="body1"
                            fontSize="0.775rem"
                            marginTop="1rem"
                            sx={{ textAlign: "left" }}>
                            By providing an email address, phone number and submitting this form, I authorize Pathway
                            Homes and its service providers to contact me at the number I provide via voice call, text,
                            artificial prerecorded voice, and ringless voicemail using an automatic telephone dialing
                            system for marketing purposes. Not a condition for purchase. Message and data rates may
                            apply. To opt-out at any time reply "STOP" or revoke your consent over phone.
                        </Typography>
                    </Paper>
                </Container>
            </Box>
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={openToster}
                autoHideDuration={6000}
                onClose={handleTosterClose}>
                <Alert onClose={handleTosterClose} severity={tosterVariant} variant="filled">
                    {tosterMessage}
                </Alert>
            </Snackbar>
        </Box>
    );

    return getPageContent(body, status, error);
};

export { ApplicantDetails };
